import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import * as Sharing from 'expo-sharing';
import * as WebBrowser from 'expo-web-browser';
import {
  AlertTriangle,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  FileText,
  Grid2x2,
  Image as ImageIcon,
  List,
  Paperclip,
  Plus,
  ScanLine,
  Search,
  Tag,
  ShieldCheck,
  Square,
  Trash2,
  X,
} from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useConfirm, useNotice } from '@/components/ConfirmDialog';
import { DocumentPreview } from '@/components/DocumentPreview';
import { InfiniteFooter } from '@/components/InfiniteFooter';
import { HScroll } from '@/components/HScroll';
import { DocPageThumb } from '@/components/DocPagePreview';
import { PdfThumb } from '@/components/PdfThumb';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { useInfiniteWindow } from '@/hooks/use-infinite-window';
import { getSystemMeta } from '@/data/systems';
import type { DocumentKind, DocumentRow, SystemRow } from '@/db/types';
import {
  DOCUMENT_KIND_LABEL,
  STORAGE_LIMIT_BYTES,
  addDocument,
  deleteDocument,
  formatBytes,
  getTotalStorageBytes,
  guessDocKindFromFilename,
  listDocumentsForProperty,
  setDocumentsKind,
} from '@/lib/documents';
import { isImageMime, persistPickedFile } from '@/lib/files';
import { goBackOrTo } from '@/lib/nav';
import { getActiveProperty, getSystemsForProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

const KIND_FILTERS: { v: DocumentKind | 'all'; label: string }[] = [
  { v: 'all', label: 'All' },
  { v: 'manual', label: 'Manuals' },
  { v: 'warranty', label: 'Warranties' },
  { v: 'receipt', label: 'Receipts' },
  { v: 'photo', label: 'Photos' },
  { v: 'invoice', label: 'Invoices' },
  { v: 'estimate', label: 'Estimates' },
  { v: 'inspection', label: 'Inspections' },
  { v: 'insurance', label: 'Insurance' },
  { v: 'permit', label: 'Permits' },
  { v: 'other', label: 'Other' },
];

export default function MediaLibrary() {
  const router = useRouter();
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const notice = useNotice();
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  // Tracked separately from docs so the meter can re-render after a
  // successful upload / delete without waiting for the doc list to
  // re-fetch. Refreshed in step with docs in `fetch()`.
  const [totalBytes, setTotalBytes] = useState(0);
  const [systems, setSystems] = useState<SystemRow[]>([]);
  const [filter, setFilter] = useState<DocumentKind | 'all'>('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'list' | 'grid'>('grid');
  const [previewDoc, setPreviewDoc] = useState<DocumentRow | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [uploadStage, setUploadStage] = useState<'pick' | 'kind'>('pick');
  // Bulk upload progress: surfaces a floating card while images / files
  // are being copied + registered so the user sees the wait time and
  // count, instead of "I picked photos but nothing happened" silence.
  const [bulkProgress, setBulkProgress] = useState<{
    done: number;
    total: number;
    label: string;
  } | null>(null);
  const [pending, setPending] = useState<{
    file_uri: string;
    file_name: string;
    mime_type: string | null;
    file_size: number | null;
  } | null>(null);
  const [pendingKind, setPendingKind] = useState<DocumentKind>('manual');
  const [pendingTitle, setPendingTitle] = useState('');
  const [pendingSystemId, setPendingSystemId] = useState<string | null>(null);
  const [systemPickerOpen, setSystemPickerOpen] = useState(false);

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    if (!prop) return;
    setPropertyId(prop.id);
    const [d, s, bytes] = await Promise.all([
      listDocumentsForProperty(db, prop.id),
      getSystemsForProperty(db, prop.id),
      getTotalStorageBytes(db, prop.id),
    ]);
    setDocs(d);
    setSystems(s);
    setTotalBytes(bytes);
  }, [db]);

  /**
   * Guard called before adding ANY new document (single, bulk, camera).
   * Returns true if the proposed bytes fit within the cap. When false,
   * surfaces a friendly notice telling the user how much they've used
   * and pauses the upload flow. Files with unknown size (file_size: null)
   * pass through — we can't accurately budget what we can't measure.
   */
  const ensureFits = useCallback(
    async (incomingBytes: number | null | undefined): Promise<boolean> => {
      if (!incomingBytes || incomingBytes <= 0) return true;
      if (totalBytes + incomingBytes <= STORAGE_LIMIT_BYTES) return true;
      await notice({
        title: 'Storage limit reached',
        message: `You're using ${formatBytes(totalBytes)} of ${formatBytes(STORAGE_LIMIT_BYTES)}. Delete a few files to make room before adding more.`,
        tone: 'warning',
      });
      return false;
    },
    [notice, totalBytes]
  );

  useFocusEffect(
    useCallback(() => {
      fetch();
    }, [fetch])
  );

  const systemById = useMemo(() => {
    const m = new Map<string, SystemRow>();
    systems.forEach((s) => m.set(s.id, s));
    return m;
  }, [systems]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (filter !== 'all' && d.kind !== filter) return false;
      if (!q) return true;
      const sysLabel = d.system_id
        ? (systemById.get(d.system_id)?.nickname ??
           getSystemMeta(systemById.get(d.system_id)?.kind)?.label ?? '')
        : '';
      return (
        d.title.toLowerCase().includes(q) ||
        (d.vendor ?? '').toLowerCase().includes(q) ||
        (d.notes ?? '').toLowerCase().includes(q) ||
        DOCUMENT_KIND_LABEL[d.kind].toLowerCase().includes(q) ||
        sysLabel.toLowerCase().includes(q)
      );
    });
  }, [docs, filter, search, systemById]);

  // Render-window for the docs list. Media tiles include image thumbs
  // that re-decode on every paint; rendering 200 of them at once locks
  // up cheap Android devices. Search runs against the full `docs`
  // array (above) so matches deep in the library still surface.
  const win = useInfiniteWindow(visible, { pageSize: 25, initialSize: 20 });

  // Warranties / insurance expiring within the next 60 days — prompt the user
  // to act before they lose coverage. Sorted soonest-first.
  const expiringSoon = useMemo(() => {
    const cutoff = Date.now() + 60 * 24 * 60 * 60 * 1000;
    return docs
      .filter((d) => d.expires_at && d.expires_at > 0 && d.expires_at <= cutoff)
      .sort((a, b) => (a.expires_at ?? 0) - (b.expires_at ?? 0));
  }, [docs]);

  // Group by document kind for the stats strip at the top.
  const kindCounts = useMemo(() => {
    const counts: Partial<Record<DocumentKind, number>> = {};
    for (const d of docs) {
      counts[d.kind] = (counts[d.kind] ?? 0) + 1;
    }
    return counts;
  }, [docs]);

  const pickFile = async () => {
    try {
      const res = await DocumentPicker.getDocumentAsync({
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (res.canceled || !res.assets || res.assets.length === 0) return;
      const a = res.assets[0];
      // Check the cap BEFORE we persist the file — saves a copy on disk
      // we'd then have to clean up, and gives the user the fastest
      // possible feedback.
      if (!(await ensureFits(a.size ?? null))) return;
      const persisted = await persistPickedFile(a.uri, a.name);
      setPending({
        file_uri: persisted,
        file_name: a.name,
        mime_type: a.mimeType ?? null,
        file_size: a.size ?? null,
      });
      setPendingTitle(a.name.replace(/\.[a-zA-Z0-9]+$/, ''));
      // Pre-select the kind from the filename (Wave 50) — defaulting
      // everything to 'manual' filed quote PDFs as manuals when users
      // breezed past the kind step.
      setPendingKind(guessDocKindFromFilename(a.name));
      setPendingSystemId(null);
      setUploadStage('kind');
    } catch (err) {
      console.warn('pickFile failed', err);
    }
  };

  const pickFromPhotos = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
    });
    if (res.canceled || !res.assets[0]) return;
    const a = res.assets[0];
    if (!(await ensureFits(a.fileSize ?? null))) return;
    const persisted = await persistPickedFile(
      a.uri,
      a.fileName ?? `photo-${Date.now()}.jpg`
    );
    setPending({
      file_uri: persisted,
      file_name: a.fileName ?? 'Photo',
      mime_type: a.mimeType ?? 'image/jpeg',
      file_size: a.fileSize ?? null,
    });
    setPendingTitle('Photo');
    setPendingKind('photo');
    setPendingSystemId(null);
    setUploadStage('kind');
  };

  const bulkPickPhotos = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
      allowsMultipleSelection: true,
    });
    if (res.canceled || res.assets.length === 0 || !propertyId) return;
    // Up-front budget check: sum the picked assets and verify the whole
    // batch fits. Cheaper than walking the loop and discovering halfway
    // through that we need to abort.
    const batchBytes = res.assets.reduce((sum, a) => sum + (a.fileSize ?? 0), 0);
    if (!(await ensureFits(batchBytes))) return;
    setUploaderOpen(false);
    const total = res.assets.length;
    setBulkProgress({ done: 0, total, label: total === 1 ? 'Saving photo…' : `Saving photo 1 of ${total}…` });
    try {
      for (let i = 0; i < res.assets.length; i++) {
        const a = res.assets[i];
        const filename = a.fileName ?? `photo-${Date.now()}.jpg`;
        const persisted = await persistPickedFile(a.uri, filename);
        await addDocument(db, {
          propertyId,
          systemId: null,
          kind: 'photo',
          title: filename.replace(/\.[a-zA-Z0-9]+$/, ''),
          file_uri: persisted,
          mime_type: a.mimeType ?? 'image/jpeg',
          file_size: a.fileSize ?? null,
        });
        // Step the progress count for visual feedback during the loop.
        const next = i + 1;
        setBulkProgress({
          done: next,
          total,
          label: next === total ? 'Wrapping up…' : `Saving photo ${next + 1} of ${total}…`,
        });
      }
    } finally {
      setBulkProgress(null);
      await fetch();
    }
  };

  const bulkPickFiles = async () => {
    try {
      const res = await DocumentPicker.getDocumentAsync({
        copyToCacheDirectory: true,
        multiple: true,
      });
      if (res.canceled || !res.assets || res.assets.length === 0 || !propertyId) return;
      const batchBytes = res.assets.reduce((sum, a) => sum + (a.size ?? 0), 0);
      if (!(await ensureFits(batchBytes))) return;
      setUploaderOpen(false);
      const total = res.assets.length;
      setBulkProgress({ done: 0, total, label: total === 1 ? 'Saving file…' : `Saving file 1 of ${total}…` });
      for (let i = 0; i < res.assets.length; i++) {
        const a = res.assets[i];
        const persisted = await persistPickedFile(a.uri, a.name);
        const kindFromMime: DocumentKind = a.mimeType?.startsWith('image/')
          ? 'photo'
          : guessDocKindFromFilename(a.name);
        await addDocument(db, {
          propertyId,
          systemId: null,
          kind: kindFromMime,
          title: a.name.replace(/\.[a-zA-Z0-9]+$/, ''),
          file_uri: persisted,
          mime_type: a.mimeType ?? null,
          file_size: a.size ?? null,
        });
        const next = i + 1;
        setBulkProgress({
          done: next,
          total,
          label: next === total ? 'Wrapping up…' : `Saving file ${next + 1} of ${total}…`,
        });
      }
      await fetch();
    } catch (err) {
      console.warn('bulkPickFiles failed', err);
    } finally {
      setBulkProgress(null);
    }
  };

  const takePhoto = async () => {
    const perm = await ImagePicker.requestCameraPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchCameraAsync({ quality: 0.7 });
    if (res.canceled || !res.assets[0]) return;
    const a = res.assets[0];
    if (!(await ensureFits(a.fileSize ?? null))) return;
    const persisted = await persistPickedFile(a.uri, `camera-${Date.now()}.jpg`);
    setPending({
      file_uri: persisted,
      file_name: 'Camera photo',
      mime_type: 'image/jpeg',
      file_size: a.fileSize ?? null,
    });
    setPendingTitle('Camera photo');
    setPendingKind('photo');
    setPendingSystemId(null);
    setUploadStage('kind');
  };

  const finalizeUpload = async () => {
    if (!pending || !propertyId) return;
    await addDocument(db, {
      propertyId,
      systemId: pendingSystemId,
      kind: pendingKind,
      title: pendingTitle.trim() || pending.file_name,
      file_uri: pending.file_uri,
      mime_type: pending.mime_type,
      file_size: pending.file_size,
    });
    setPending(null);
    setPendingTitle('');
    setPendingSystemId(null);
    setUploaderOpen(false);
    setUploadStage('pick');
    await fetch();
  };

  const openDoc = (d: DocumentRow) => {
    if (selectMode) {
      setSelected((prev) => {
        const next = new Set(prev);
        next.has(d.id) ? next.delete(d.id) : next.add(d.id);
        return next;
      });
      return;
    }
    setPreviewDoc(d);
  };

  // Long-press (native) / right-click (web) → per-file actions menu
  // (Wave 50). In select mode a long-press just keeps toggling.
  const [menuDoc, setMenuDoc] = useState<DocumentRow | null>(null);
  const longPressDoc = (d: DocumentRow) => {
    if (selectMode) return;
    setMenuDoc(d);
  };

  /** Same download semantics as the preview modal: browser save-file
   *  on web, share sheet (→ Save to Files) on native. */
  const downloadDoc = async (d: DocumentRow) => {
    try {
      if (Platform.OS === 'web') {
        const safe = d.title.replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '-') || 'homestead-file';
        const ext = d.mime_type?.includes('pdf')
          ? '.pdf'
          : isImageMime(d.mime_type)
          ? '.jpg'
          : '';
        // eslint-disable-next-line no-undef
        const a = document.createElement('a');
        a.href = d.file_uri;
        a.download = `${safe}${ext}`;
        // eslint-disable-next-line no-undef
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(d.file_uri);
      }
    } catch (err) {
      console.warn('download failed', err);
    }
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  // Bulk re-file (Wave 49): move every selected file to a new kind.
  const [rekindOpen, setRekindOpen] = useState(false);
  const rekindSelected = async (kind: DocumentKind) => {
    if (selected.size === 0) return;
    await setDocumentsKind(db, [...selected], kind);
    setRekindOpen(false);
    exitSelectMode();
    await fetch();
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    const count = selected.size;
    const ok = await confirm({
      title: `Delete ${count} file${count === 1 ? '' : 's'}?`,
      message: "This can't be undone.",
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    for (const id of selected) {
      await deleteDocument(db, id);
    }
    exitSelectMode();
    await fetch();
  };

  const deleteSingle = async (d: DocumentRow) => {
    const ok = await confirm({
      title: 'Delete file?',
      message: `“${d.title}” will be removed.`,
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    await deleteDocument(db, d.id);
    await fetch();
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/(tabs)')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>
          {selectMode ? `${selected.size} selected` : 'Files'}
        </Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          {selectMode ? (
            <>
              <Pressable
                onPress={() => setRekindOpen(true)}
                disabled={selected.size === 0}
                accessibilityLabel="Re-file selected"
                style={({ pressed }) => [
                  styles.iconBtn,
                  selected.size === 0 && { opacity: 0.4 },
                  pressed && { opacity: 0.85 },
                ]}>
                <Tag size={16} color={Palette.forest} strokeWidth={2} />
              </Pressable>
              <Pressable
                onPress={deleteSelected}
                disabled={selected.size === 0}
                style={({ pressed }) => [
                  styles.deleteBtn,
                  selected.size === 0 && { opacity: 0.4 },
                  pressed && { opacity: 0.85 },
                ]}>
                <Trash2 size={16} color={Palette.cream} strokeWidth={2.2} />
              </Pressable>
              <Pressable
                onPress={exitSelectMode}
                style={({ pressed }) => [styles.iconBtn, pressed && { opacity: 0.85 }]}>
                <X size={16} color={Palette.ink} strokeWidth={2.2} />
              </Pressable>
            </>
          ) : (
            <>
              <Pressable
                onPress={() => setSelectMode(true)}
                style={({ pressed }) => [styles.iconBtn, pressed && { opacity: 0.85 }]}>
                <CheckSquare size={16} color={Palette.forest} strokeWidth={2} />
              </Pressable>
              <Pressable
                onPress={() => {
                  setUploadStage('pick');
                  setUploaderOpen(true);
                }}
                style={({ pressed }) => [styles.addBtn, pressed && { opacity: 0.85 }]}>
                <Plus size={18} color={Palette.cream} strokeWidth={2.2} />
              </Pressable>
            </>
          )}
        </View>
      </View>

      <View style={styles.searchBox}>
        <Search size={14} color={Palette.inkFaint} strokeWidth={1.8} />
        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder="Search by title, vendor, system…"
          placeholderTextColor={Palette.inkFaint}
          style={[styles.searchInput, webNoOutline]}
        />
      </View>

      <HScroll style={styles.filterStrip} contentStyle={styles.filterStripContent}>
        {KIND_FILTERS.map((f) => {
          const sel = filter === f.v;
          return (
            <Pressable
              key={f.v}
              onPress={() => setFilter(f.v)}
              style={({ pressed }) => [
                styles.filterChip,
                sel && styles.filterChipSel,
                pressed && { opacity: 0.9 },
              ]}>
              <Text style={[styles.filterChipText, sel && styles.filterChipTextSel]}>
                {f.label}
              </Text>
            </Pressable>
          );
        })}
      </HScroll>

      <ScrollView
        contentContainerStyle={{ paddingBottom: 60 }}
        showsVerticalScrollIndicator={false}
        onScroll={win.onScroll}
        onLayout={win.onLayout}
        scrollEventThrottle={16}>
        {/* Smart Scan — the "just add the paper" fast path. OCR reads
            the photo and files it as an expense or quote automatically
            (Wave 47). Regular uploads via + still work for manuals,
            warranties, and anything else. */}
        <Pressable
          onPress={() => router.push('/scan' as never)}
          style={({ pressed }) => [styles.smartScanBanner, pressed && { opacity: 0.92 }]}>
          <View style={styles.smartScanIcon}>
            <ScanLine size={18} color={Palette.cream} strokeWidth={2} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.smartScanTitle}>Smart scan</Text>
            <Text style={styles.smartScanSub}>
              Snap any receipt, invoice, or estimate — auto-filed in the right place.
            </Text>
          </View>
          <ChevronRight size={16} color={Palette.forest} strokeWidth={2} />
        </Pressable>

        {/* Storage meter hidden while everything is device-local (Wave
            59, user call) — a "1 GB cap" reads like a cloud quota we
            don't have yet. Bring <StorageMeter usedBytes={totalBytes} />
            back when accounts + cloud sync ship. The soft cap itself
            still guards uploads via ensureRoomFor. */}

        {/* Stats strip — quick read on what's in your vault */}
        {docs.length > 0 ? (
          <View style={styles.statsCard}>
            <View style={styles.statsBig}>
              <Text style={styles.statsBigNum}>{docs.length}</Text>
              <Text style={styles.statsBigLabel}>Total files</Text>
            </View>
            <HScroll contentStyle={styles.statsChips}>
              {(Object.keys(kindCounts) as DocumentKind[]).map((k) => (
                <View key={k} style={styles.statChip}>
                  <Text style={styles.statChipNum}>{kindCounts[k]}</Text>
                  <Text style={styles.statChipLabel}>{DOCUMENT_KIND_LABEL[k]}</Text>
                </View>
              ))}
            </HScroll>
          </View>
        ) : null}

        {/* Expiration alerts */}
        {expiringSoon.length > 0 ? (
          <View style={styles.alertCard}>
            <View style={styles.alertHead}>
              <AlertTriangle size={14} color={Palette.honeyDeep} strokeWidth={2} />
              <Text style={styles.alertHeadText}>EXPIRING IN THE NEXT 60 DAYS</Text>
            </View>
            {expiringSoon.slice(0, 3).map((d) => {
              const days = Math.max(
                0,
                Math.round(((d.expires_at ?? 0) - Date.now()) / 86_400_000)
              );
              return (
                <Pressable
                  key={d.id}
                  onPress={() => openDoc(d)}
                  style={({ pressed }) => [styles.alertRow, pressed && { opacity: 0.85 }]}>
                  <ShieldCheck size={14} color={Palette.honeyDeep} strokeWidth={1.7} />
                  <Text style={styles.alertText} numberOfLines={1}>
                    <Text style={styles.alertBold}>{d.title}</Text>
                    {` — ${days === 0 ? 'today' : `in ${days} ${days === 1 ? 'day' : 'days'}`}`}
                  </Text>
                  <ChevronRight size={14} color={Palette.honeyDeep} strokeWidth={1.7} />
                </Pressable>
              );
            })}
          </View>
        ) : null}

        <View style={styles.countRow}>
          <Text style={styles.count}>{visible.length} of {docs.length} files</Text>
          <View style={styles.viewToggle}>
            <Pressable
              onPress={() => setView('grid')}
              style={({ pressed }) => [
                styles.viewBtn,
                view === 'grid' && styles.viewBtnSel,
                pressed && { opacity: 0.8 },
              ]}>
              <Grid2x2 size={14} color={view === 'grid' ? Palette.cream : Palette.ink} strokeWidth={1.8} />
            </Pressable>
            <Pressable
              onPress={() => setView('list')}
              style={({ pressed }) => [
                styles.viewBtn,
                view === 'list' && styles.viewBtnSel,
                pressed && { opacity: 0.8 },
              ]}>
              <List size={14} color={view === 'list' ? Palette.cream : Palette.ink} strokeWidth={1.8} />
            </Pressable>
          </View>
        </View>

        {visible.length === 0 ? (
          <View style={styles.empty}>
            <Paperclip size={36} color={Palette.inkFaint} strokeWidth={1.4} />
            <Text style={styles.emptyTitle}>
              {docs.length === 0 ? 'Nothing uploaded yet' : 'No matches'}
            </Text>
            <Text style={styles.emptyBody}>
              {docs.length === 0
                ? 'Tap + above to add a manual, warranty, receipt, or photo. Everything stays organized by system and kind.'
                : 'Try a different filter or clear the search.'}
            </Text>
          </View>
        ) : view === 'grid' ? (
          <View style={styles.grid}>
            {win.visible.map((d) => {
              const system = d.system_id ? systemById.get(d.system_id) : null;
              const meta = system ? getSystemMeta(system.kind) : null;
              const isSel = selected.has(d.id);
              return (
                <Pressable
                  key={d.id}
                  onPress={() => openDoc(d)}
                  onLongPress={() => longPressDoc(d)}
                  {...({
                    onContextMenu: (e: { preventDefault?: () => void }) => {
                      e?.preventDefault?.();
                      longPressDoc(d);
                    },
                  } as object)}
                  style={({ pressed }) => [
                    styles.gridCell,
                    isSel && styles.gridCellSel,
                    pressed && { transform: [{ scale: 0.97 }] },
                  ]}>
                  <View style={styles.gridThumbWrap}>
                    {isImageMime(d.mime_type) ? (
                      <Image source={{ uri: d.file_uri }} style={styles.gridThumb} />
                    ) : d.mime_type?.includes('pdf') ? (
                      <PdfThumb
                        uri={d.file_uri}
                        style={styles.gridThumb}
                        fallback={
                          <View style={[styles.gridThumb, styles.gridIconBox]}>
                            <FileText size={34} color={Palette.forest} strokeWidth={1.5} />
                            <View style={styles.extBadge}>
                              <Text style={styles.extBadgeText}>PDF</Text>
                            </View>
                            <Text style={styles.gridIconHint}>Tap to preview</Text>
                          </View>
                        }
                      />
                    ) : (
                      <DocPageThumb
                        uri={d.file_uri}
                        mime={d.mime_type}
                        name={d.file_uri}
                        style={styles.gridThumb}
                        fallback={
                          <View style={[styles.gridThumb, styles.gridIconBox]}>
                            <FileText size={34} color={Palette.forest} strokeWidth={1.5} />
                            <View style={styles.extBadge}>
                              <Text style={styles.extBadgeText}>
                                {(d.file_uri.match(/\.([a-zA-Z0-9]{2,5})$/)?.[1] ?? 'FILE').toUpperCase()}
                              </Text>
                            </View>
                            <Text style={styles.gridIconHint}>Tap to preview</Text>
                          </View>
                        }
                      />
                    )}
                    {selectMode ? (
                      <View style={[styles.gridCheck, isSel && styles.gridCheckOn]}>
                        {isSel ? (
                          <CheckSquare size={14} color={Palette.cream} strokeWidth={2.4} />
                        ) : (
                          <Square size={14} color={Palette.cream} strokeWidth={2} />
                        )}
                      </View>
                    ) : null}
                  </View>
                  {/* Kind label rendered inline (not overlaid on the thumb)
                      so it can never be clipped by image rounded corners or
                      parent overflow rules — issue surfaced when toggling
                      grid/list views in some browsers. */}
                  <View style={styles.gridKindRow}>
                    <View style={styles.gridKindPill}>
                      <Text style={styles.gridKindPillText} numberOfLines={1}>
                        {DOCUMENT_KIND_LABEL[d.kind]}
                      </Text>
                    </View>
                  </View>
                  <Text style={styles.gridTitle} numberOfLines={1}>{d.title}</Text>
                  <Text style={styles.gridSys} numberOfLines={1}>
                    {(system ? system.nickname ?? meta?.label ?? system.kind : 'Property-wide') +
                      (d.cost_cents != null
                        ? ` · $${(d.cost_cents / 100).toLocaleString()}`
                        : '') +
                      ` · ${new Date(d.created_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}`}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ) : (
          <View style={styles.list}>
            {win.visible.map((d) => {
              const system = d.system_id ? systemById.get(d.system_id) : null;
              const meta = system ? getSystemMeta(system.kind) : null;
              const isSel = selected.has(d.id);
              return (
                <Pressable
                  key={d.id}
                  onPress={() => openDoc(d)}
                  onLongPress={() => longPressDoc(d)}
                  {...({
                    onContextMenu: (e: { preventDefault?: () => void }) => {
                      e?.preventDefault?.();
                      longPressDoc(d);
                    },
                  } as object)}
                  style={({ pressed }) => [
                    styles.row,
                    isSel && styles.rowSel,
                    pressed && { transform: [{ scale: 0.99 }] },
                  ]}>
                  {selectMode ? (
                    <View style={[styles.listCheck, isSel && styles.listCheckOn]}>
                      {isSel ? (
                        <CheckSquare size={14} color={Palette.cream} strokeWidth={2.4} />
                      ) : (
                        <Square size={14} color={Palette.forest} strokeWidth={2} />
                      )}
                    </View>
                  ) : null}
                  {isImageMime(d.mime_type) ? (
                    <Image source={{ uri: d.file_uri }} style={styles.thumb} />
                  ) : d.mime_type?.includes('pdf') ? (
                    <PdfThumb
                      uri={d.file_uri}
                      style={styles.thumb}
                      fallback={
                        <View style={styles.iconBox}>
                          <FileText size={22} color={Palette.forest} strokeWidth={1.7} />
                        </View>
                      }
                    />
                  ) : (
                    <DocPageThumb
                      uri={d.file_uri}
                      mime={d.mime_type}
                      name={d.file_uri}
                      style={styles.thumb}
                      fallback={
                        <View style={styles.iconBox}>
                          <FileText size={22} color={Palette.forest} strokeWidth={1.7} />
                        </View>
                      }
                    />
                  )}
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.title} numberOfLines={1}>{d.title}</Text>
                    <Text style={styles.meta} numberOfLines={1}>
                      {DOCUMENT_KIND_LABEL[d.kind]}
                      {d.cost_cents != null ? ` · $${(d.cost_cents / 100).toLocaleString()}` : ''}
                      {` · ${new Date(d.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
                      {system ? ` · ${system.nickname ?? meta?.label ?? system.kind}` : ''}
                      {d.expires_at ? ` · expires ${new Date(d.expires_at).toLocaleDateString()}` : ''}
                    </Text>
                    {d.notes && !d.title.includes(d.notes) ? (
                      <Text style={styles.meta} numberOfLines={1}>{d.notes}</Text>
                    ) : null}
                  </View>
                  {selectMode ? null : (
                    <Pressable
                      onPress={() => deleteSingle(d)}
                      hitSlop={8}
                      style={({ pressed }) => [styles.trashBtn, pressed && { opacity: 0.7 }]}>
                      <Trash2 size={15} color={Palette.clay} strokeWidth={1.7} />
                    </Pressable>
                  )}
                </Pressable>
              );
            })}
          </View>
        )}
        {visible.length > 0 ? (
          <InfiniteFooter hasMore={win.hasMore} total={win.total} loadMore={win.loadMore} noun="file" />
        ) : null}
      </ScrollView>

      {/* Full-screen preview */}
      <DocumentPreview
        doc={previewDoc}
        systemLabel={
          previewDoc?.system_id
            ? systemById.get(previewDoc.system_id)?.nickname ??
              getSystemMeta(systemById.get(previewDoc.system_id)?.kind)?.label ?? null
            : null
        }
        onClose={() => setPreviewDoc(null)}
      />

      {/* Upload source picker */}
      <Modal
        animationType="fade"
        transparent
        visible={uploaderOpen && uploadStage === 'pick'}
        onRequestClose={() => setUploaderOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setUploaderOpen(false)}>
          <View style={styles.sheet}>
            <View style={styles.sheetHead}>
              <Text style={styles.sheetTitle}>Add to library</Text>
              <Pressable onPress={() => setUploaderOpen(false)} hitSlop={8}>
                <X size={18} color={Palette.inkSoft} strokeWidth={1.8} />
              </Pressable>
            </View>
            {/* Smart Scan leads (Wave 52) — the auto-filing path is the
                one most people should take; manual picks stay below for
                files that don't need reading. */}
            <Pressable
              onPress={() => {
                setUploaderOpen(false);
                router.push('/scan' as never);
              }}
              style={({ pressed }) => [styles.sheetOpt, styles.sheetOptFeatured, pressed && { opacity: 0.85 }]}>
              <ScanLine size={20} color={Palette.cream} strokeWidth={1.9} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.sheetOptText, { color: Palette.cream }]}>Smart scan</Text>
                <Text style={[styles.sheetOptSub, { color: 'rgba(251,246,234,0.8)' }]}>
                  Reads it and files it — quotes, receipts, labels, records
                </Text>
              </View>
            </Pressable>
            <Pressable onPress={pickFile} style={({ pressed }) => [styles.sheetOpt, pressed && { opacity: 0.85 }]}>
              <Paperclip size={20} color={Palette.forest} strokeWidth={1.7} />
              <Text style={styles.sheetOptText}>Pick a file (PDF, doc, image)</Text>
            </Pressable>
            <Pressable onPress={pickFromPhotos} style={({ pressed }) => [styles.sheetOpt, pressed && { opacity: 0.85 }]}>
              <ImageIcon size={20} color={Palette.forest} strokeWidth={1.7} />
              <Text style={styles.sheetOptText}>Pick from photos</Text>
            </Pressable>
            {Platform.OS !== 'web' ? (
              <Pressable onPress={takePhoto} style={({ pressed }) => [styles.sheetOpt, pressed && { opacity: 0.85 }]}>
                <ImageIcon size={20} color={Palette.forest} strokeWidth={1.7} />
                <Text style={styles.sheetOptText}>Take a photo</Text>
              </Pressable>
            ) : null}

            <Text style={styles.sheetGroup}>BULK</Text>
            <Pressable onPress={bulkPickPhotos} style={({ pressed }) => [styles.sheetOpt, pressed && { opacity: 0.85 }]}>
              <ImageIcon size={20} color={Palette.forest} strokeWidth={1.7} />
              <View style={{ flex: 1 }}>
                <Text style={styles.sheetOptText}>Add many photos</Text>
                <Text style={styles.sheetOptSub}>Multi-select; each saved as a photo</Text>
              </View>
            </Pressable>
            <Pressable onPress={bulkPickFiles} style={({ pressed }) => [styles.sheetOpt, pressed && { opacity: 0.85 }]}>
              <Paperclip size={20} color={Palette.forest} strokeWidth={1.7} />
              <View style={{ flex: 1 }}>
                <Text style={styles.sheetOptText}>Add many files</Text>
                <Text style={styles.sheetOptSub}>Multi-select; filed by filename — re-file any time</Text>
              </View>
            </Pressable>
          </View>
        </Pressable>
      </Modal>

      {/* Kind + title + optional system */}
      <Modal
        animationType="fade"
        transparent
        visible={uploadStage === 'kind'}
        onRequestClose={() => {
          setUploadStage('pick');
          setPending(null);
        }}>
        <Pressable
          style={styles.backdrop}
          onPress={() => {
            setUploadStage('pick');
            setPending(null);
          }}>
          <Pressable style={styles.sheetWide} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.sheetTitle}>What kind of document?</Text>
            <ScrollView style={{ maxHeight: 280 }}>
              {(Object.keys(DOCUMENT_KIND_LABEL) as DocumentKind[]).map((k) => {
                const sel = pendingKind === k;
                return (
                  <Pressable
                    key={k}
                    onPress={() => setPendingKind(k)}
                    style={[styles.kindRow, sel && styles.kindRowSel]}>
                    <Text style={styles.kindLabel}>{DOCUMENT_KIND_LABEL[k]}</Text>
                  </Pressable>
                );
              })}
            </ScrollView>

            <Text style={[styles.sheetFieldLabel, { marginTop: 12 }]}>TITLE</Text>
            <TextInput
              value={pendingTitle}
              onChangeText={setPendingTitle}
              placeholder="e.g. Furnace manual"
              placeholderTextColor={Palette.inkFaint}
              style={[styles.sheetFieldInput, webNoOutline]}
            />

            <Text style={[styles.sheetFieldLabel, { marginTop: 12 }]}>
              ATTACH TO SYSTEM (optional)
            </Text>
            <Pressable
              onPress={() => setSystemPickerOpen(true)}
              style={({ pressed }) => [styles.sheetFieldInput, pressed && { opacity: 0.92 }]}>
              <Text style={[styles.systemPickText, !pendingSystemId && styles.systemPickPlaceholder]}>
                {pendingSystemId
                  ? systems.find((s) => s.id === pendingSystemId)?.nickname ??
                    getSystemMeta(systems.find((s) => s.id === pendingSystemId)?.kind)?.label ??
                    'System'
                  : 'Property-wide (no specific system)'}
              </Text>
            </Pressable>

            <Pressable
              onPress={finalizeUpload}
              style={({ pressed }) => [styles.saveBtn, pressed && { transform: [{ scale: 0.98 }] }]}>
              <Text style={styles.saveText}>Save</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>

      {/* System picker */}
      <Modal
        animationType="fade"
        transparent
        visible={systemPickerOpen}
        onRequestClose={() => setSystemPickerOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setSystemPickerOpen(false)}>
          <Pressable style={styles.sheetWide} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.sheetTitle}>Attach to a system</Text>
            <ScrollView style={{ maxHeight: 360 }}>
              <Pressable
                onPress={() => {
                  setPendingSystemId(null);
                  setSystemPickerOpen(false);
                }}
                style={[styles.kindRow, !pendingSystemId && styles.kindRowSel]}>
                <Text style={styles.kindLabel}>Property-wide (no specific system)</Text>
              </Pressable>
              {systems.map((s) => {
                const meta = getSystemMeta(s.kind);
                const sel = pendingSystemId === s.id;
                return (
                  <Pressable
                    key={s.id}
                    onPress={() => {
                      setPendingSystemId(s.id);
                      setSystemPickerOpen(false);
                    }}
                    style={[styles.kindRow, sel && styles.kindRowSel]}>
                    <Text style={styles.kindLabel}>
                      {s.nickname ?? meta?.label ?? s.kind}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Floating progress card during bulk uploads. Stays out of the
          way at the bottom but shows the current step + count so the
          user doesn't think the app froze. Auto-dismisses on completion. */}
      {bulkProgress ? (
        <View style={styles.uploadToast} pointerEvents="box-none">
          <View style={styles.uploadCard}>
            <ActivityIndicator color={Palette.forest} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.uploadLabel} numberOfLines={1}>
                {bulkProgress.label}
              </Text>
              <View style={styles.uploadTrack}>
                <View
                  style={[
                    styles.uploadFill,
                    {
                      width: `${Math.round(
                        (bulkProgress.done / Math.max(1, bulkProgress.total)) * 100
                      )}%`,
                    },
                  ]}
                />
              </View>
            </View>
            <Text style={styles.uploadCount}>
              {bulkProgress.done}/{bulkProgress.total}
            </Text>
          </View>
        </View>
      ) : null}

      {/* Per-file actions menu (Wave 50) — long-press / right-click. */}
      <Modal
        animationType="fade"
        transparent
        visible={menuDoc !== null}
        onRequestClose={() => setMenuDoc(null)}>
        <Pressable style={styles.rekindBackdrop} onPress={() => setMenuDoc(null)}>
          <Pressable style={styles.rekindCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.rekindTitle} numberOfLines={2}>
              {menuDoc?.title}
            </Text>
            {(
              [
                {
                  label: 'Preview',
                  run: () => menuDoc && setPreviewDoc(menuDoc),
                },
                {
                  label: Platform.OS === 'web' ? 'Download' : 'Save / share',
                  run: () => menuDoc && void downloadDoc(menuDoc),
                },
                {
                  label: 'Re-file as…',
                  run: () => {
                    if (!menuDoc) return;
                    setSelected(new Set([menuDoc.id]));
                    setRekindOpen(true);
                  },
                },
                {
                  label: 'Select multiple',
                  run: () => {
                    if (!menuDoc) return;
                    setSelectMode(true);
                    setSelected(new Set([menuDoc.id]));
                  },
                },
                {
                  label: 'Delete',
                  tint: Palette.clay,
                  run: () => menuDoc && void deleteSingle(menuDoc),
                },
              ] as { label: string; tint?: string; run: () => void }[]
            ).map((a) => (
              <Pressable
                key={a.label}
                onPress={() => {
                  setMenuDoc(null);
                  setTimeout(a.run, 120);
                }}
                style={({ pressed, hovered }) => [
                  styles.rekindRow,
                  (pressed || hovered) && { backgroundColor: '#EEF3EE' },
                ]}>
                <Text style={[styles.rekindRowText, a.tint ? { color: a.tint } : null]}>
                  {a.label}
                </Text>
              </Pressable>
            ))}
          </Pressable>
        </Pressable>
      </Modal>

      {/* Bulk re-file kind picker (Wave 49). */}
      <Modal
        animationType="fade"
        transparent
        visible={rekindOpen}
        onRequestClose={() => setRekindOpen(false)}>
        <Pressable style={styles.rekindBackdrop} onPress={() => setRekindOpen(false)}>
          <Pressable style={styles.rekindCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.rekindTitle}>
              Re-file {selected.size} {selected.size === 1 ? 'file' : 'files'} as…
            </Text>
            <ScrollView style={{ maxHeight: 380 }}>
              {(Object.keys(DOCUMENT_KIND_LABEL) as DocumentKind[]).map((k) => (
                <Pressable
                  key={k}
                  onPress={() => void rekindSelected(k)}
                  style={({ pressed, hovered }) => [
                    styles.rekindRow,
                    (pressed || hovered) && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <Text style={styles.rekindRowText}>{DOCUMENT_KIND_LABEL[k]}</Text>
                </Pressable>
              ))}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}

/**
 * Styled storage progress meter. Top-of-page anchor on Media & Files —
 * tells the user at a glance how much of their 1 GB cap they're using
 * and shifts color tone as they approach the limit so it reads as
 * "comfortably under" → "watch this" → "act now" without ever being
 * alarming.
 *
 * Color tiers:
 *   • Under 80% — forest (calm, on-brand)
 *   • 80–95%    — honey (gentle attention)
 *   • Over 95%  — clay  (act soon, but still warm — no red, ever)
 */
function StorageMeter({ usedBytes }: { usedBytes: number }) {
  const cap = STORAGE_LIMIT_BYTES;
  const pct = Math.max(0, Math.min(100, (usedBytes / cap) * 100));
  const fillColor =
    pct >= 95 ? Palette.clay : pct >= 80 ? Palette.honey : Palette.forest;
  const pctText = pct < 1 && usedBytes > 0 ? '<1' : Math.round(pct).toString();
  return (
    <View style={styles.storageCard}>
      <View style={styles.storageHead}>
        <Text style={styles.storageLabel}>STORAGE</Text>
        <Text style={[styles.storagePct, { color: fillColor }]}>{pctText}% used</Text>
      </View>
      <View style={styles.storageBarTrack}>
        <View
          style={[
            styles.storageBarFill,
            { width: `${Math.max(2, pct)}%`, backgroundColor: fillColor },
          ]}
        />
      </View>
      <Text style={styles.storageSub}>
        {formatBytes(usedBytes)} of {formatBytes(cap)}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  smartScanBanner: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#EAF0EA',
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 16,
    padding: 13,
  },
  smartScanIcon: {
    width: 38, height: 38, borderRadius: 12,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  sheetOptFeatured: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  extBadge: {
    marginTop: 6,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: Palette.forest,
  },
  extBadgeText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.cream,
    letterSpacing: 0.6,
  },
  gridIconHint: {
    marginTop: 5,
    fontFamily: Fonts.sansMedium,
    fontSize: 11,
    color: Palette.inkFaint,
  },
  rekindBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  rekindCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 18, width: '100%', maxWidth: 360,
  },
  rekindTitle: {
    fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink, marginBottom: 10,
  },
  rekindRow: { paddingVertical: 11, paddingHorizontal: 12, borderRadius: 10 },
  rekindRowText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  smartScanTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  smartScanSub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    lineHeight: 16,
  },
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    backgroundColor: Palette.card, borderWidth: 1.5,
    borderColor: Palette.line, borderRadius: 11,
    paddingHorizontal: 11,
  },
  searchInput: {
    flex: 1, paddingVertical: 10,
    fontFamily: Fonts.sansSemibold, fontSize: 13, color: Palette.ink,
  },
  // Explicit height on the horizontal ScrollView is critical on RN Web —
  // without it the chips were getting clipped on the bottom because the
  // ScrollView's measured height was collapsing below the chip's content
  // size. flexShrink: 0 keeps it from being squeezed by the verticals.
  filterStrip: {
    marginTop: 10,
    height: 44,
    flexGrow: 0,
    flexShrink: 0,
  },
  filterStripContent: {
    paddingHorizontal: Spacing.four,
    alignItems: 'center',
    gap: 6,
  },
  filterChip: {
    height: 32,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterChipSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  filterChipText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink },
  filterChipTextSel: { color: Palette.cream },
  count: {
    fontFamily: Fonts.sansSemibold, fontSize: 13, color: Palette.inkFaint,
  },
  countRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginHorizontal: Spacing.four,
    marginTop: 10,
  },
  viewToggle: {
    flexDirection: 'row',
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 8,
    overflow: 'hidden',
  },
  viewBtn: {
    width: 32,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewBtnSel: { backgroundColor: Palette.forest },
  // Storage meter — sits above the stats card. Cream surface, line
  // border, and an 8px progress bar tinted by usage tier. Always visible
  // (even at 0%) so the 1 GB cap is communicated up-front.
  storageCard: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  storageHead: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  storageLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 1,
  },
  storagePct: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    letterSpacing: -0.2,
  },
  storageBarTrack: {
    marginTop: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Palette.sandDeep,
    overflow: 'hidden',
  },
  storageBarFill: {
    height: '100%',
    borderRadius: 4,
  },
  storageSub: {
    marginTop: 7,
    fontFamily: Fonts.sansSemibold,
    fontSize: 12.5,
    color: Palette.inkSoft,
    letterSpacing: 0.2,
  },
  statsCard: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  statsBig: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    borderRightWidth: 1,
    borderRightColor: Palette.line,
    paddingRight: 12,
  },
  statsBigNum: {
    fontFamily: Fonts.serifBold,
    fontSize: 26,
    color: Palette.forest,
    letterSpacing: -0.5,
  },
  statsBigLabel: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 11,
    color: Palette.inkFaint,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    marginTop: 2,
  },
  statsChips: { gap: 6, paddingRight: 4 },
  statChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    minWidth: 56,
    marginRight: 6,
  },
  statChipNum: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest },
  statChipLabel: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 11,
    color: Palette.inkSoft,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    marginTop: 1,
  },
  alertCard: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    padding: 12,
    borderRadius: 14,
    backgroundColor: Palette.honeySoft,
    borderWidth: 1,
    borderColor: 'rgba(225,163,61,0.4)',
  },
  alertHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginBottom: 6,
  },
  alertHeadText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.honeyDeep,
    letterSpacing: 0.5,
  },
  alertRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingVertical: 6,
  },
  alertText: { flex: 1, fontFamily: Fonts.sansSemibold, fontSize: 13.5, color: '#5A4012' },
  alertBold: { fontFamily: Fonts.sansBold, color: '#3A2A06' },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 14,
    marginTop: 8,
    gap: 8,
  },
  gridCell: {
    width: '47%',
    flexGrow: 1,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 14,
    padding: 8,
    gap: 5,
  },
  gridThumbWrap: { position: 'relative' },
  gridThumb: {
    width: '100%',
    aspectRatio: 4 / 3,
    borderRadius: 10,
  },
  gridIconBox: {
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gridKindRow: {
    flexDirection: 'row',
    paddingHorizontal: 4,
    marginTop: 6,
  },
  gridKindPill: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: '#EAF0EA',
    borderWidth: 1,
    borderColor: 'rgba(45,71,40,0.16)',
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  gridKindPillText: {
    fontFamily: Fonts.sansBold,
    fontSize: 10.5,
    color: Palette.forest,
    letterSpacing: 0.45,
    textTransform: 'uppercase',
  },
  gridTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
    paddingHorizontal: 4,
    marginTop: 2,
  },
  gridSys: {
    fontFamily: Fonts.sansMedium,
    fontSize: 11.5,
    color: Palette.inkSoft,
    paddingHorizontal: 4,
  },
  empty: {
    alignItems: 'center', paddingHorizontal: 32, paddingVertical: 56, gap: 10,
  },
  emptyTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink },
  emptyBody: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    textAlign: 'center', lineHeight: 19,
  },
  list: { paddingHorizontal: 18, marginTop: 8, gap: 8 },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: 14, padding: 11,
  },
  thumb: { width: 44, height: 44, borderRadius: 11 },
  iconBox: {
    width: 44, height: 44, borderRadius: 11,
    backgroundColor: '#EAF0EA', alignItems: 'center', justifyContent: 'center',
  },
  title: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  meta: {
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    marginTop: 2,
  },
  jumpBtn: { paddingHorizontal: 6 },
  jumpText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest },
  trashBtn: { padding: 6 },
  iconBtn: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  deleteBtn: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.clay,
    alignItems: 'center', justifyContent: 'center',
  },
  gridCellSel: { borderColor: Palette.forest, borderWidth: 2 },
  gridCheck: {
    position: 'absolute', top: 6, right: 6,
    width: 24, height: 24, borderRadius: 12,
    backgroundColor: 'rgba(42,37,31,0.6)',
    alignItems: 'center', justifyContent: 'center',
  },
  gridCheckOn: { backgroundColor: Palette.forest },
  rowSel: { borderColor: Palette.forest, borderWidth: 2 },
  listCheck: {
    width: 22, height: 22, borderRadius: 11,
    borderWidth: 2, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  listCheckOn: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  addBtn: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  sheet: {
    backgroundColor: Palette.sand, borderRadius: Radius.xl,
    padding: Spacing.four, width: '100%', maxWidth: 360, gap: 8,
  },
  sheetWide: {
    backgroundColor: Palette.sand, borderRadius: Radius.xl,
    padding: Spacing.four, width: '100%', maxWidth: 420,
  },
  sheetHead: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 8,
  },
  sheetTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink },
  sheetOpt: {
    flexDirection: 'row', alignItems: 'center', gap: 11,
    paddingVertical: 14, paddingHorizontal: 14,
    backgroundColor: Palette.card, borderRadius: 12,
    borderWidth: 1, borderColor: Palette.line,
  },
  sheetOptText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  sheetOptSub: {
    marginTop: 1,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
  sheetGroup: {
    marginTop: 8, marginLeft: 4,
    fontFamily: Fonts.sansBold, fontSize: 11, color: Palette.inkFaint,
    letterSpacing: 0.7,
  },
  kindRow: {
    paddingVertical: 12, paddingHorizontal: 12,
    borderRadius: 10, marginBottom: 4,
  },
  kindRowSel: { backgroundColor: '#EEF3EE' },
  kindLabel: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  sheetFieldLabel: {
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  sheetFieldInput: {
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.card, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 10,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  systemPickText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  systemPickPlaceholder: { color: Palette.inkFaint, fontFamily: Fonts.sansMedium },
  saveBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: Palette.forest, paddingVertical: 14,
    borderRadius: 14, marginTop: 14,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 14.5 },
  uploadToast: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 24,
    alignItems: 'center',
    paddingHorizontal: Spacing.four,
  },
  uploadCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    maxWidth: 420,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 16,
    paddingVertical: 12,
    paddingHorizontal: 14,
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
  },
  uploadLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
  },
  uploadTrack: {
    marginTop: 6,
    height: 4,
    borderRadius: 2,
    backgroundColor: Palette.sandDeep,
    overflow: 'hidden',
  },
  uploadFill: {
    height: '100%',
    backgroundColor: Palette.forest,
    borderRadius: 2,
  },
  uploadCount: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.forest,
    letterSpacing: 0.3,
  },
});
